import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 + Consent Mode v2 bridge.
 *
 * The gtag stub is initialised in `/public/index.html` with all non-essential
 * storage denied by default (GDPR-safe). This hook:
 *   1. Watches the Capexara cookie-consent event and calls
 *      `gtag('consent', 'update', ...)` when the visitor accepts / rejects.
 *   2. Fires a manual `page_view` on every SPA route change (gtag's own
 *      `send_page_view` is disabled so history.pushState does not miss events).
 *
 * No PII is ever sent; IPs are anonymised at the GA4 level.
 */
const GA_ID = "G-6QMQ7VZBV8";
const STORAGE_KEY = "capexara_cookie_consent";

function applyConsentFromStorage() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const cats = parsed?.categories;
    if (!cats) return;
    window.gtag("consent", "update", {
      analytics_storage: cats.analytics ? "granted" : "denied",
      ad_storage: cats.marketing ? "granted" : "denied",
      ad_user_data: cats.marketing ? "granted" : "denied",
      ad_personalization: cats.marketing ? "granted" : "denied",
    });
  } catch {
    /* ignore malformed consent payload */
  }
}

export function useAnalytics() {
  const { pathname, search } = useLocation();

  // Keep gtag consent state in sync with the Capexara banner.
  useEffect(() => {
    applyConsentFromStorage();
    const handler = () => applyConsentFromStorage();
    window.addEventListener("capexara:consent-change", handler);
    window.addEventListener("storage", (e) => {
      if (e.key === STORAGE_KEY) applyConsentFromStorage();
    });
    return () => {
      window.removeEventListener("capexara:consent-change", handler);
    };
  }, []);

  // Track SPA route changes as page_views.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const page_path = `${pathname}${search || ""}`;
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA_ID,
    });
  }, [pathname, search]);
}
